import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { AvatarUpload } from '@/components/profile/avatar-upload';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { CompanyCombobox } from '@/components/ui/company-combobox';
import { 
  Sparkles, 
  ArrowRight, 
  CheckCircle, 
  Building, 
  GraduationCap, 
  Briefcase,
  Mail,
  Phone,
  MapPin,
  Users,
  Share2,
  Target,
  Star,
  Calendar
} from 'lucide-react';

const StepIcon = ({ icon: Icon }: { icon: React.ElementType }) => (
  <Icon className="h-5 w-5 text-primary" />
);

const STEPS = [
  {
    id: 1,
    title: 'Basic Information',
    description: 'Tell us about yourself',
    icon: Building,
    requiredFields: ['full_name', 'title', 'location']
  },
  {
    id: 2,
    title: 'Career Goals', 
    description: 'What are you looking for?',
    icon: Target,
    requiredFields: ['job_preferences.role', 'job_preferences.industry']
  },
  {
    id: 3,
    title: 'Work Experience',
    description: 'Share your current role',
    icon: Briefcase,
    requiredFields: ['current_experience.title', 'current_experience.company']
  },
  {
    id: 4,
    title: 'Education',
    description: 'Add your education history',
    icon: GraduationCap,
    requiredFields: ['education.institution', 'education.degree']
  },
  {
    id: 5,
    title: 'Skills',
    description: 'Add your key skills',
    icon: Star,
    requiredFields: ['skills']
  },
  {
    id: 6,
    title: 'Contact Details',
    description: 'How can we reach you?',
    icon: Mail,
    requiredFields: ['email']
  }
];

export default function OnboardingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState({
    full_name: '',
    title: '',
    email: '',
    phone: '',
    location: '',
    avatar_url: '',
    summary: '',
    job_preferences: {
      role: '',
      industry: '',
      location_preference: '',
      remote_preference: 'hybrid',
      salary_range: '',
      job_type: 'full-time'
    },
    current_experience: {
      title: '',
      company: '',
      company_id: '',
      start_date: '',
      end_date: '',
      current: true
    },
    education: {
      institution: '',
      degree: '',
      field_of_study: '',
      start_date: '',
      end_date: '',
      current: false
    },
    skills: [],
    notification_preferences: {
      email_notifications: true,
      phone_notifications: false,
      marketing_emails: true
    }
  });

  const totalSteps = STEPS.length;
  const progress = (step / totalSteps) * 100;

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();

        if (error) throw error;

        if (data) {
          setProfile(prev => ({
            ...prev,
            ...data,
            job_preferences: data.job_preferences || prev.job_preferences,
          }));
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      }
    };

    fetchProfile();
  }, [user]);

  const calculateCompletionPercentage = () => {
    let completedFields = 0;
    let totalFields = 0;

    STEPS.forEach(step => {
      step.requiredFields.forEach(field => {
        totalFields++;
        const value = field.split('.').reduce((obj, key) => obj?.[key], profile);
        if (value && (typeof value === 'string' ? value.trim() !== '' : true)) {
          completedFields++;
        }
      });
    });

    return Math.round((completedFields / totalFields) * 100);
  };

  const updateProfile = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const currentStepFields = STEPS[step - 1].requiredFields;
      const hasRequiredFields = currentStepFields.every(field => {
        const value = field.split('.').reduce((obj, key) => obj?.[key], profile);
        return value && (typeof value === 'string' ? value.trim() !== '' : true);
      });

      if (!hasRequiredFields) {
        toast({
          variant: 'destructive',
          title: 'Missing Information',
          description: 'Please fill in all required fields before continuing.',
        });
        return;
      }

      // Update profile in database
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          full_name: profile.full_name,
          title: profile.title,
          email: profile.email,
          phone: profile.phone,
          location: profile.location,
          avatar_url: profile.avatar_url,
          summary: profile.summary,
          job_preferences: profile.job_preferences,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (profileError) throw profileError;

      // If we're on the work experience step, add the experience
      if (step === 3 && profile.current_experience.title && profile.current_experience.company) {
        const { error: experienceError } = await supabase
          .from('experiences')
          .insert([{
            profile_id: user.id,
            position: profile.current_experience.title,
            company_id: profile.current_experience.company_id || null,
            company_name: profile.current_experience.company,
            start_date: profile.current_experience.start_date || new Date().toISOString().split('T')[0],
            end_date: profile.current_experience.current ? null : profile.current_experience.end_date,
            current: profile.current_experience.current,
          }]);

        if (experienceError) throw experienceError;
      }

      // If we're on the education step, add the education
      if (step === 4 && profile.education.institution && profile.education.degree) {
        const { error: educationError } = await supabase
          .from('education')
          .insert([{
            profile_id: user.id,
            institution: profile.education.institution,
            degree: profile.education.degree,
            field_of_study: profile.education.field_of_study,
            start_date: profile.education.start_date,
            end_date: profile.education.end_date,
            current: profile.education.current,
          }]);

        if (educationError) throw educationError;
      }

      // If we're on the skills step, add the skills
      if (step === 5 && profile.skills.length > 0) {
        const { error: skillsError } = await supabase
          .from('skills')
          .insert(profile.skills.map(skill => ({
            profile_id: user.id,
            name: skill,
            category: 'Other',
          })));

        if (skillsError) throw skillsError;
      }

      if (step === totalSteps) {
        const completionPercentage = calculateCompletionPercentage();
        
        toast({
          title: 'Profile Started!',
          description: `Your profile is ${completionPercentage}% complete. Continue adding information to make it stand out.`,
        });
        
        navigate('/profile/' + user.id);
      } else {
        setStep(step + 1);
      }
    } catch (error: any) {
      console.error('Error updating profile:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to update profile. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    if (step === totalSteps) {
      const completionPercentage = calculateCompletionPercentage();
      toast({
        title: 'Profile Started',
        description: `Your profile is ${completionPercentage}% complete. Add more details to improve visibility.`,
      });
      navigate('/profile/' + user.id);
    } else {
      setStep(step + 1);
    }
  };

  return (
    <div className="min-h-screen bg-muted/30 flex flex-col py-12 px-4 sm:px-6 lg:px-8">
      {/* Progress Steps */}
      <div className="max-w-3xl mx-auto w-full mb-8">
        <div className="flex items-center justify-between mb-4">
          {STEPS.map((s, i) => (
            <div 
              key={s.id} 
              className={`flex flex-col items-center ${i < STEPS.length - 1 ? 'flex-1' : ''}`}
            >
              <div 
                className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${
                  step >= s.id ? 'bg-primary text-primary-foreground' : 'bg-muted'
                }`}
              >
                <s.icon className="h-5 w-5" />
              </div>
              <div className="text-center">
                <p className={`text-sm font-medium ${step >= s.id ? 'text-primary' : 'text-muted-foreground'}`}>
                  {s.title}
                </p>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`h-1 flex-1 mt-2 ${step > s.id ? 'bg-primary' : 'bg-muted'}`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center">
        <div className="w-full max-w-3xl space-y-8">
          <Card className="border-primary/10">
            <CardHeader>
              <div className="flex items-center gap-2">
                {STEPS[step - 1].icon && <StepIcon icon={STEPS[step - 1].icon} />}
                <div>
                  <CardTitle>{STEPS[step - 1].title}</CardTitle>
                  <CardDescription>{STEPS[step - 1].description}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {step === 1 && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Full Name</label>
                    <Input
                      value={profile.full_name}
                      onChange={(e) => setProfile({ ...profile, full_name: e.target.value })}
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Professional Title</label>
                    <Input
                      value={profile.title}
                      onChange={(e) => setProfile({ ...profile, title: e.target.value })}
                      placeholder="Software Engineer"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Location</label>
                    <Input
                      value={profile.location}
                      onChange={(e) => setProfile({ ...profile, location: e.target.value })}
                      placeholder="San Francisco, CA"
                    />
                  </div>
                  <div className="relative">
                    <Avatar className="h-32 w-32 mx-auto">
                      <AvatarImage src={profile.avatar_url} />
                      <AvatarFallback>
                        {profile.full_name?.[0] || user?.email?.[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="absolute bottom-0 right-1/2 translate-x-16">
                      <AvatarUpload
                        userId={user!.id}
                        onUploadComplete={(url) => setProfile({ ...profile, avatar_url: url })}
                      />
                    </div>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Desired Role</label>
                    <Input
                      value={profile.job_preferences.role}
                      onChange={(e) => setProfile({
                        ...profile,
                        job_preferences: { ...profile.job_preferences, role: e.target.value }
                      })}
                      placeholder="Senior Software Engineer"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Industry</label>
                    <Select
                      value={profile.job_preferences.industry}
                      onValueChange={(value) => setProfile({
                        ...profile,
                        job_preferences: { ...profile.job_preferences, industry: value }
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select industry" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="technology">Technology</SelectItem>
                        <SelectItem value="finance">Finance</SelectItem>
                        <SelectItem value="healthcare">Healthcare</SelectItem>
                        <SelectItem value="education">Education</SelectItem>
                        <SelectItem value="retail">Retail</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Location Preference</label>
                    <Input
                      value={profile.job_preferences.location_preference}
                      onChange={(e) => setProfile({
                        ...profile,
                        job_preferences: { ...profile.job_preferences, location_preference: e.target.value }
                      })}
                      placeholder="San Francisco Bay Area"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Work Type</label>
                    <Select
                      value={profile.job_preferences.remote_preference}
                      onValueChange={(value) => setProfile({
                        ...profile,
                        job_preferences: { ...profile.job_preferences, remote_preference: value }
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select work type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="onsite">On-site</SelectItem>
                        <SelectItem value="hybrid">Hybrid</SelectItem>
                        <SelectItem value="remote">Remote</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Current Title</label>
                    <Input
                      value={profile.current_experience.title}
                      onChange={(e) => setProfile({
                        ...profile,
                        current_experience: { ...profile.current_experience, title: e.target.value }
                      })}
                      placeholder="Senior Software Engineer"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Company</label>
                    <CompanyCombobox 
                      value={profile.current_experience.company_id}
                      onChange={(value) => {
                        setProfile({
                          ...profile,
                          current_experience: { 
                            ...profile.current_experience, 
                            company_id: value,
                            company: ''
                          }
                        });
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Start Date</label>
                    <Input
                      type="date"
                      value={profile.current_experience.start_date}
                      onChange={(e) => setProfile({
                        ...profile,
                        current_experience: { ...profile.current_experience, start_date: e.target.value }
                      })}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={profile.current_experience.current}
                      onCheckedChange={(checked) => setProfile({
                        ...profile,
                        current_experience: { ...profile.current_experience, current: checked }
                      })}
                    />
                    <label className="text-sm font-medium">I currently work here</label>
                  </div>
                  {!profile.current_experience.current && (
                    <div>
                      <label className="block text-sm font-medium mb-1">End Date</label>
                      <Input
                        type="date"
                        value={profile.current_experience.end_date}
                        onChange={(e) => setProfile({
                          ...profile,
                          current_experience: { ...profile.current_experience, end_date: e.target.value }
                        })}
                      />
                    </div>
                  )}
                </div>
              )}

              {step === 4 && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Institution</label>
                    <Input
                      value={profile.education.institution}
                      onChange={(e) => setProfile({
                        ...profile,
                        education: { ...profile.education, institution: e.target.value }
                      })}
                      placeholder="University Name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Degree</label>
                    <Input
                      value={profile.education.degree}
                      onChange={(e) => setProfile({
                        ...profile,
                        education: { ...profile.education, degree: e.target.value }
                      })}
                      placeholder="Bachelor of Science"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Field of Study</label>
                    <Input
                      value={profile.education.field_of_study}
                      onChange={(e) => setProfile({
                        ...profile,
                        education: { ...profile.education, field_of_study: e.target.value }
                      })}
                      placeholder="Computer Science"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Start Date</label>
                    <Input
                      type="date"
                      value={profile.education.start_date}
                      onChange={(e) => setProfile({
                        ...profile,
                        education: { ...profile.education, start_date: e.target.value }
                      })}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={profile.education.current}
                      onCheckedChange={(checked) => setProfile({
                        ...profile,
                        education: { ...profile.education, current: checked }
                      })}
                    />
                    <label className="text-sm font-medium">I'm currently studying here</label>
                  </div>
                  {!profile.education.current && (
                    <div>
                      <label className="block text-sm font-medium mb-1">End Date</label>
                      <Input
                        type="date"
                        value={profile.education.end_date}
                        onChange={(e) => setProfile({
                          ...profile,
                        education: { ...profile.education, end_date: e.target.value }
                        })}
                      />
                    </div>
                  )}
                </div>
              )}

              {step === 5 && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Skills</label>
                    <div className="space-y-2">
                      <Input
                        placeholder="Add a skill (e.g., JavaScript, Project Management)"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && e.currentTarget.value) {
                            e.preventDefault();
                            setProfile({
                              ...profile,
                              skills: [...profile.skills, e.currentTarget.value]
                            });
                            e.currentTarget.value = '';
                          }
                        }}
                      />
                      <div className="flex flex-wrap gap-2">
                        {profile.skills.map((skill, index) => (
                          <Badge 
                            key={index}
                            variant="secondary"
                            className="cursor-pointer"
                            onClick={() => {
                              setProfile({
                                ...profile,
                                skills: profile.skills.filter((_, i) => i !== index)
                              });
                            }}
                          >
                            {skill} ×
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {step === 6 && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Email Address</label>
                    <Input
                      type="email"
                      value={profile.email}
                      onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                      placeholder="john@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Phone Number</label>
                    <Input
                      type="tel"
                      value={profile.phone}
                      onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                  <div className="space-y-4 mt-6">
                    <h3 className="font-medium">Notification Preferences</h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <label className="text-sm font-medium">Email Notifications</label>
                          <p className="text-sm text-muted-foreground">
                            Receive job matches and updates via email
                          </p>
                        </div>
                        <Switch
                          checked={profile.notification_preferences.email_notifications}
                          onCheckedChange={(checked) => setProfile({
                            ...profile,
                            notification_preferences: { ...profile.notification_preferences, email_notifications: checked }
                          })}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <label className="text-sm font-medium">Phone Notifications</label>
                          <p className="text-sm text-muted-foreground">
                            Receive updates via SMS
                          </p>
                        </div>
                        <Switch
                          checked={profile.notification_preferences.phone_notifications}
                          onCheckedChange={(checked) => setProfile({
                            ...profile,
                            notification_preferences: { ...profile.notification_preferences, phone_notifications: checked }
                          })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-between mt-6">
                <Button
                  variant="ghost"
                  onClick={handleSkip}
                >
                  Skip for now
                </Button>
                <Button
                  onClick={updateProfile}
                  disabled={loading}
                  className="min-w-[120px]"
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      <span>Saving...</span>
                    </div>
                  ) : step === totalSteps ? (
                    <div className="flex items-center gap-2">
                      <span>Complete</span>
                      <CheckCircle className="h-4 w-4" />
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span>Continue</span>
                      <ArrowRight className="h-4 w-4" />
                    </div>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}